import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Switch } from '../../ui/switch';
import { Alert, AlertDescription } from '../../ui/alert';
import { Loader2, AlertCircle } from 'lucide-react';
import { userService } from '../../../_services';
import GrandparentConfirmDialog from '../Group_no/GrandparentConfirmDialog.jsx';

const US_STATES = [
    { name: 'Please select state', value: '' },
    { name: 'Alabama', value: 'AL' }, { name: 'Alaska', value: 'AK' }, { name: 'Arizona', value: 'AZ' },
    { name: 'Arkansas', value: 'AR' }, { name: 'California', value: 'CA' }, { name: 'Colorado', value: 'CO' },
    { name: 'Connecticut', value: 'CT' }, { name: 'Delaware', value: 'DE' }, { name: 'Florida', value: 'FL' },
    { name: 'Georgia', value: 'GA' }, { name: 'Hawaii', value: 'HI' }, { name: 'Idaho', value: 'ID' },
    { name: 'Illinois', value: 'IL' }, { name: 'Indiana', value: 'IN' }, { name: 'Iowa', value: 'IA' },
    { name: 'Kansas', value: 'KS' }, { name: 'Kentucky', value: 'KY' }, { name: 'Louisiana', value: 'LA' },
    { name: 'Maine', value: 'ME' }, { name: 'Maryland', value: 'MD' }, { name: 'Massachusetts', value: 'MA' },
    { name: 'Michigan', value: 'MI' }, { name: 'Minnesota', value: 'MN' }, { name: 'Mississippi', value: 'MS' },
    { name: 'Missouri', value: 'MO' }, { name: 'Montana', value: 'MT' }, { name: 'Nebraska', value: 'NE' },
    { name: 'Nevada', value: 'NV' }, { name: 'New Hampshire', value: 'NH' }, { name: 'New Jersey', value: 'NJ' },
    { name: 'New Mexico', value: 'NM' }, { name: 'New York', value: 'NY' }, { name: 'North Carolina', value: 'NC' },
    { name: 'North Dakota', value: 'ND' }, { name: 'Ohio', value: 'OH' }, { name: 'Oklahoma', value: 'OK' },
    { name: 'Oregon', value: 'OR' }, { name: 'Pennsylvania', value: 'PA' }, { name: 'Rhode Island', value: 'RI' },
    { name: 'South Carolina', value: 'SC' }, { name: 'South Dakota', value: 'SD' }, { name: 'Tennessee', value: 'TN' },
    { name: 'Texas', value: 'TX' }, { name: 'Utah', value: 'UT' }, { name: 'Vermont', value: 'VT' },
    { name: 'Virginia', value: 'VA' }, { name: 'Washington', value: 'WA' }, { name: 'West Virginia', value: 'WV' },
    { name: 'Wisconsin', value: 'WI' }, { name: 'Wyoming', value: 'WY' }
];

function EditUserDialog({ open, onOpenChange, repid, onSuccess }) {
    const [formData, setFormData] = useState({
        UserName: '',
        Fname: '',
        Phone: '',
        Email: '',
        Address1: '',
        Country: 'United States',
        State: '',
        City: '',
        Zip: '',
        Commission: '',
        SecLevelCommission: '',
        AllowForGC: false,
        OverrideCommission: ''
    });
    
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [submitMessage, setSubmitMessage] = useState('');
    const [grandparentPopup, setGrandparentPopup] = useState(false);
    const [grandparentsList, setGrandparentsList] = useState([]);
    const [gpLoading, setGpLoading] = useState(false);

    useEffect(() => {
        if (open && repid) {
            fetchUserDetails();
        }
    }, [open, repid]);

    const fetchUserDetails = async () => {
        const Usermeta_Data = window.atob(localStorage.getItem('ajs_user_id'));
        const [UserId, UserName] = Usermeta_Data.split(':');

        try {
            const apidata = await userService.DetailOfUser(UserId, UserName, repid);
            
            if (apidata && apidata.length > 0) {
                const user = apidata[0];
                setFormData({
                    UserName: user.userName || '',
                    Fname: user.fullName || '',
                    Phone: user.phoneNumber || '',
                    Email: user.emailAddress || '',
                    Address1: user.address || '',
                    Country: 'United States',
                    State: user.state || '',
                    City: user.city || '',
                    Zip: user.zipCode || '',
                    Commission: user.commission || '',
                    SecLevelCommission: user.parentSubdistComm || '',
                    AllowForGC: user.isAllow2LevelCommission || false,
                    OverrideCommission: user.grandparentCommission || ''
                });
            }
        } catch (error) {
            console.error('Error fetching user details:', error);
            setSubmitMessage('Error loading user details');
        }
    };

    const handleInputChange = (name, value) => {
        setFormData(prev => ({ ...prev, [name]: value }));
        setErrors(prev => ({ ...prev, [name]: '' }));
        setSubmitMessage('');
    };

    const validate = () => {
        const newErrors = {};
        
        if (!formData.UserName) newErrors.UserName = 'Username is required';
        else if (/^[0-9]+$/.test(formData.UserName)) newErrors.UserName = 'This Username not allowed';
        
        if (!formData.Fname) newErrors.Fname = 'Full Name is required';
        if (!formData.Phone) newErrors.Phone = 'Phone Number is required';
        if (!formData.Address1) newErrors.Address1 = 'Address is required';
        if (!formData.State) newErrors.State = 'State is required';
        if (!formData.City) newErrors.City = 'City is required';
        if (!formData.Zip) newErrors.Zip = 'Zip Code is required';
        
        if (!formData.Commission) newErrors.Commission = 'Commission is required';
        else if (parseFloat(formData.Commission) < 0) newErrors.Commission = 'Commission cannot be negative';
        
        if (formData.AllowForGC && !formData.SecLevelCommission) {
            newErrors.SecLevelCommission = '2nd Level Commission is required';
        }

        return newErrors;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        const newErrors = validate();
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        setLoading(true);
        setSubmitMessage('');

        const Usermeta_Data = window.atob(localStorage.getItem('ajs_user_id'));
        const [UserId, UserName] = Usermeta_Data.split(':');

        try {
            // Validate phone first
            const phoneValidation = await userService.GetPhoneNoValidate(formData.Phone);
            if (phoneValidation && phoneValidation[0]?.code !== "1") {
                setErrors(prev => ({ ...prev, Phone: 'Invalid Phone Number' }));
                setLoading(false);
                return;
            }

            // Update user
            const apidata = await userService.UpdateUser(
                UserId,
                UserName,
                repid,
                formData.UserName,
                formData.Fname,
                formData.Phone,
                formData.Address1,
                formData.Country,
                formData.State,
                formData.City,
                formData.Zip,
                formData.Commission,
                formData.SecLevelCommission,
                formData.AllowForGC,
                formData.OverrideCommission
            );

            if (apidata && apidata.length > 0) {
                if (apidata[0].code === "1") {
                    setSubmitMessage('User updated successfully');
                    if (onSuccess) onSuccess();
                    setTimeout(() => onOpenChange(false), 1000);
                } else {
                    setSubmitMessage(apidata[0].message || 'Error updating user');
                }
            }
        } catch (error) {
            console.error('Error updating user:', error);
            setSubmitMessage('Error updating user');
        } finally {
            setLoading(false);
        }
    };

    const handleSecondLevelToggle = (checked) => {
        handleInputChange('AllowForGC', checked);
        if (checked) {
            fetchGrandparentUsers();
        }
    };

    const fetchGrandparentUsers = async () => {
        setGpLoading(true);
        const Usermeta_Data = window.atob(localStorage.getItem('ajs_user_id'));
        const [UserId, UserName] = Usermeta_Data.split(':');

        try {
            const apidata = await userService.GetUsersForGrandParent(UserId, UserName, repid);
            if (apidata && apidata.length > 0) {
                setGrandparentsList(apidata);
                setGrandparentPopup(true);
            }
        } catch (error) {
            console.error('Error fetching grandparent users:', error);
        } finally {
            setGpLoading(false);
        }
    };

    const handleAddGrandParent = async () => {
        setGpLoading(true);
        const Usermeta_Data = window.atob(localStorage.getItem('ajs_user_id'));
        const [UserId, UserName] = Usermeta_Data.split(':');

        try {
            const apidata = await userService.AddGrandParent(UserId, UserName, repid);
            if (apidata && apidata[0]?.code === "1") {
                setGrandparentPopup(false);
                setSubmitMessage('Grandparent assigned successfully');
            }
        } catch (error) {
            console.error('Error adding grandparent:', error);
        } finally {
            setGpLoading(false);
        }
    };

    return (
        <>
            <Dialog open={open} onOpenChange={onOpenChange}>
                <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
                    <DialogHeader>
                        <DialogTitle className="text-xl font-semibold text-brand-dark">
                            Edit User
                        </DialogTitle>
                    </DialogHeader>

                    <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto pr-2">
                        <div className="grid grid-cols-2 gap-4 py-4">
                            <div className="space-y-2">
                                <Label htmlFor="UserName">Username *</Label>
                                <Input
                                    id="UserName"
                                    value={formData.UserName}
                                    onChange={(e) => handleInputChange('UserName', e.target.value)}
                                    className={errors.UserName ? 'border-red-500' : ''}
                                />
                                {errors.UserName && <p className="text-sm text-red-500">{errors.UserName}</p>}
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="Fname">Full Name *</Label>
                                <Input
                                    id="Fname"
                                    value={formData.Fname}
                                    onChange={(e) => handleInputChange('Fname', e.target.value)}
                                    className={errors.Fname ? 'border-red-500' : ''}
                                />
                                {errors.Fname && <p className="text-sm text-red-500">{errors.Fname}</p>}
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="Phone">Phone *</Label>
                                <Input
                                    id="Phone"
                                    value={formData.Phone}
                                    onChange={(e) => handleInputChange('Phone', e.target.value)}
                                    className={errors.Phone ? 'border-red-500' : ''}
                                />
                                {errors.Phone && <p className="text-sm text-red-500">{errors.Phone}</p>}
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="Email">Email</Label>
                                <Input
                                    id="Email"
                                    type="email"
                                    value={formData.Email}
                                    disabled
                                    className="bg-gray-100"
                                />
                            </div>

                            <div className="space-y-2 col-span-2">
                                <Label htmlFor="Address1">Address *</Label>
                                <Input
                                    id="Address1"
                                    value={formData.Address1}
                                    onChange={(e) => handleInputChange('Address1', e.target.value)}
                                    className={errors.Address1 ? 'border-red-500' : ''}
                                />
                                {errors.Address1 && <p className="text-sm text-red-500">{errors.Address1}</p>}
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="State">State *</Label>
                                <Select value={formData.State} onValueChange={(value) => handleInputChange('State', value)}>
                                    <SelectTrigger className={errors.State ? 'border-red-500' : ''}>
                                        <SelectValue placeholder="Select State" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {US_STATES.map((state) => (
                                            <SelectItem key={state.value} value={state.value}>{state.name}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                {errors.State && <p className="text-sm text-red-500">{errors.State}</p>}
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="City">City *</Label>
                                <Input
                                    id="City"
                                    value={formData.City}
                                    onChange={(e) => handleInputChange('City', e.target.value)}
                                    className={errors.City ? 'border-red-500' : ''}
                                />
                                {errors.City && <p className="text-sm text-red-500">{errors.City}</p>}
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="Zip">Zip Code *</Label>
                                <Input
                                    id="Zip"
                                    value={formData.Zip}
                                    onChange={(e) => handleInputChange('Zip', e.target.value)}
                                    className={errors.Zip ? 'border-red-500' : ''}
                                />
                                {errors.Zip && <p className="text-sm text-red-500">{errors.Zip}</p>}
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="Commission">Commission *</Label>
                                <Input
                                    id="Commission"
                                    type="number"
                                    step="0.01"
                                    value={formData.Commission}
                                    onChange={(e) => handleInputChange('Commission', e.target.value)}
                                    className={errors.Commission ? 'border-red-500' : ''}
                                />
                                {errors.Commission && <p className="text-sm text-red-500">{errors.Commission}</p>}
                            </div>

                            <div className="space-y-2 col-span-2">
                                <div className="flex items-center space-x-2">
                                    <Switch
                                        id="AllowForGC"
                                        checked={formData.AllowForGC}
                                        onCheckedChange={handleSecondLevelToggle}
                                        disabled={gpLoading}
                                    />
                                    <Label htmlFor="AllowForGC" className="cursor-pointer">
                                        Allow for 2nd Level Commission
                                    </Label>
                                    {gpLoading && <Loader2 className="h-4 w-4 animate-spin" />}
                                </div>
                            </div>

                            {formData.AllowForGC && (
                                <>
                                    <div className="space-y-2">
                                        <Label htmlFor="SecLevelCommission">2nd Level Commission *</Label>
                                        <Input
                                            id="SecLevelCommission"
                                            type="number"
                                            step="0.01"
                                            value={formData.SecLevelCommission}
                                            onChange={(e) => handleInputChange('SecLevelCommission', e.target.value)}
                                            className={errors.SecLevelCommission ? 'border-red-500' : ''}
                                        />
                                        {errors.SecLevelCommission && <p className="text-sm text-red-500">{errors.SecLevelCommission}</p>}
                                    </div>

                                    <div className="space-y-2">
                                        <Label htmlFor="OverrideCommission">Override Commission</Label>
                                        <Input
                                            id="OverrideCommission"
                                            type="number"
                                            step="0.01"
                                            value={formData.OverrideCommission}
                                            onChange={(e) => handleInputChange('OverrideCommission', e.target.value)}
                                        />
                                    </div>
                                </>
                            )}
                        </div>

                        {submitMessage && (
                            <Alert variant={submitMessage.includes('success') ? 'default' : 'destructive'} className="mt-4">
                                <AlertCircle className="h-4 w-4" />
                                <AlertDescription>{submitMessage}</AlertDescription>
                            </Alert>
                        )}

                        <DialogFooter className="mt-6">
                            <Button
                                type="button"
                                variant="default"
                                onClick={() => onOpenChange(false)}
                                disabled={loading}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                variant="secondary"
                                disabled={loading}
                            >
                                {loading ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Saving...</> : 'Save Changes'}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            <GrandparentConfirmDialog
                open={grandparentPopup}
                onOpenChange={setGrandparentPopup}
                title="Confirm Grandparent Assignment"
                grandparentsList={grandparentsList}
                onConfirm={handleAddGrandParent}
                loading={gpLoading}
            />
        </>
    );
}

export default EditUserDialog;
